import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  en: {
    translation: {
      // Auth
      "auth.login": "Sign in",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.submit": "Sign in",
      "auth.error": "Invalid credentials",
      "auth.welcome": "Welcome",
      "auth.subtitle": "Sign in to access the IQES IQ-APP AI Estimator",
      "auth.demo": "Demo admin: admin@iqeslowvoltage.com / IqesAdmin2026!",
      // Nav
      "nav.dashboard": "Dashboard",
      "nav.projects": "Projects",
      "nav.prices": "Price Library",
      "nav.references": "References",
      "nav.settings": "Settings",
      "nav.logout": "Sign out",
      "nav.crm": "CRM",
      // Dashboard
      "dash.title": "Dashboard",
      "dash.subtitle": "Operational overview",
      "dash.total_projects": "Projects",
      "dash.total_estimations": "Estimations",
      "dash.in_progress": "In progress",
      "dash.completed": "Completed",
      "dash.revenue": "Revenue forecast",
      "dash.recent": "Recent projects",
      "dash.new_project": "New project",
      "dash.no_projects": "No projects yet. Create your first one.",
      // Projects
      "proj.title": "Projects",
      "proj.new": "New project",
      "proj.name": "Project name",
      "proj.client": "Client",
      "proj.location": "Location",
      "proj.description": "Description",
      "proj.create": "Create",
      "proj.cancel": "Cancel",
      "proj.search": "Search by name or client…",
      "proj.open": "Open",
      "proj.delete": "Delete",
      "proj.status": "Status",
      "proj.updated": "Updated",
      "proj.confirm_delete": "Delete this project? This cannot be undone.",
      // Project detail
      "pd.upload": "Upload blueprint",
      "pd.upload_hint": "Drop a PDF, PNG or JPG here, or click to browse.",
      "pd.processing": "Processing blueprint…",
      "pd.demo_mode": "DEMO MODE",
      "pd.demo_note": "Detections are simulated until the YOLO model is deployed.",
      "pd.tab_viewer": "Blueprint",
      "pd.tab_bom": "BOM",
      "pd.tab_schedule": "Schedule",
      "pd.tab_compliance": "Compliance",
      "pd.tab_ai": "AI Analysis",
      "pd.tab_legend": "Legend",
      "pd.tab_kmz": "KMZ Map",
      "pd.tab_plankmz": "Plan + KMZ",
      "pd.tab_survey": "Site Survey",
      "pd.tab_route": "AI Route",
      // Legend panel
      "leg.title": "Plan legend · auto-extracted",
      "leg.ready": "Ready",
      "leg.running": "Extracting… (60-90s)",
      "leg.error_prefix": "Error: ",
      "leg.extract": "Extract legend",
      "leg.re_extract": "Re-extract",
      "leg.auto_count": "Auto-count 100%",
      "leg.final_report": "Generate final report",
      "leg.aps_bom": "Native BOM (APS)",
      "leg.filter_all": "All",
      "leg.filter_electrical": "Electrical",
      "leg.filter_lv": "Low Voltage",
      "leg.add": "Add",
      "leg.save_changes": "Save changes",
      "leg.col_symbol": "Symbol",
      "leg.col_desc": "Description",
      "leg.col_category": "Category",
      "leg.col_subsystem": "Subsystem",
      "leg.col_code": "Code",
      "leg.empty_none": 'Legend not extracted yet. Click "Extract legend" above.',
      "leg.empty_filter": "No symbols match this filter.",
      "leg.notes_title": "Technical notes extracted",
      "leg.notes_impact": "affect BOM",
      "leg.apply_rules": "Apply rules to BOM",
      "leg.counting": "Counting symbols automatically… (60-120s)",
      "leg.counted_ok": "symbols counted",
      "leg.bom_generated": "BOM lines generated",
      "leg.of_raw": "(of {{raw}} raw)",
      "leg.pipeline_stage": "Full pipeline · stage:",
      "leg.pipeline_ready": "Final report generated",
      "leg.pipeline_symbols": "symbols",
      "leg.pipeline_rules": "rules applied",
      "leg.mat_total": "Material total:",
      "leg.aps_mapping": "Mapping native blocks to legend…",
      "leg.aps_mapped": "blocks mapped",
      "leg.aps_counted": "objects counted natively",
      "leg.aps_first": "Upload a DWG/RVT first and run APS Phase 2 (extract-blocks).",
      "leg.remove_confirm": "Remove this symbol from the legend?",
      "leg.vendor_none": "—",
      "leg.symbols_count": "symbols",
      "leg.notes_count": "notes",
      "leg.toggle_overlay": "Show hits on plan",
      "leg.hide_overlay": "Hide hits",
      // KMZ panel
      "kmz.title": "KMZ · Google Earth (fiber/data network)",
      "kmz.layers_loaded": "layer(s) loaded",
      "kmz.upload": "Upload KMZ",
      "kmz.placemarks": "Placemarks",
      "kmz.paths": "Paths",
      "kmz.polygons": "Polygons",
      "kmz.total_ft": "Total ft",
      "kmz.empty": "Upload a .kmz or .kml file from Google Earth to view the fiber/data network on the map.",
      "kmz.points": "points",
      "kmz.node": "Node",
      "kmz.path": "Path",
      "pd.detections": "Detections",
      "pd.routing": "Routing",
      "pd.linear_ft": "linear ft",
      "pd.materials": "Materials",
      "pd.labor": "Labor",
      "pd.grand_total": "Grand total",
      "pd.run_ai": "Run AI analysis",
      "pd.ai_running": "Analyzing with Claude Sonnet 4.5…",
      "pd.export_pdf": "Export PDF",
      "pd.export_csv": "Export CSV",
      "pd.export_json": "Export JSON",
      "pd.push_crm": "Push to CRM",
      "pd.new_estimate": "New estimation",
      "pd.annotate": "Annotator",
      "pd.compare": "Compare",
      "common.compare": "Compare",
      // BOM
      "bom.category": "Category",
      "bom.item": "Item",
      "bom.qty": "Qty",
      "bom.unit": "Unit",
      "bom.unit_cost": "Unit cost",
      "bom.total": "Total",
      "bom.save": "Save changes",
      "bom.saved": "BOM saved",
      // Schedule
      "sched.phase": "Phase",
      "sched.duration": "Duration",
      "sched.notes": "Notes",
      // AI
      "ai.summary": "Executive summary",
      "ai.compliance": "Compliance notes",
      "ai.opt": "Optimization suggestions",
      "ai.risks": "Risk flags",
      "ai.empty": "No AI analysis yet. Run it to get compliance + optimization insights.",
      // Prices
      "prices.title": "Price Library",
      "prices.subtitle": "Edit unit material & labor costs by component.",
      "prices.save": "Save",
      "prices.saved": "Price updated",
      // Settings
      "set.title": "Settings",
      "set.crm": "CRM Integration",
      "set.crm_url": "CRM URL",
      "set.crm_status": "Link status",
      "set.language": "Language",
    },
  },
  es: {
    translation: {
      "auth.login": "Iniciar sesión",
      "auth.email": "Correo electrónico",
      "auth.password": "Contraseña",
      "auth.submit": "Ingresar",
      "auth.error": "Credenciales inválidas",
      "auth.welcome": "Bienvenido",
      "auth.subtitle": "Inicia sesión para acceder al AI Estimator IQES IQ-APP",
      "auth.demo": "Admin demo: admin@iqeslowvoltage.com / IqesAdmin2026!",
      "nav.dashboard": "Panel",
      "nav.projects": "Proyectos",
      "nav.prices": "Precios",
      "nav.references": "Referencias",
      "nav.settings": "Ajustes",
      "nav.logout": "Cerrar sesión",
      "nav.crm": "CRM",
      "dash.title": "Panel",
      "dash.subtitle": "Resumen operativo",
      "dash.total_projects": "Proyectos",
      "dash.total_estimations": "Estimaciones",
      "dash.in_progress": "En curso",
      "dash.completed": "Completados",
      "dash.revenue": "Ingresos estimados",
      "dash.recent": "Proyectos recientes",
      "dash.new_project": "Nuevo proyecto",
      "dash.no_projects": "Aún no hay proyectos. Crea el primero.",
      "proj.title": "Proyectos",
      "proj.new": "Nuevo proyecto",
      "proj.name": "Nombre del proyecto",
      "proj.client": "Cliente",
      "proj.location": "Ubicación",
      "proj.description": "Descripción",
      "proj.create": "Crear",
      "proj.cancel": "Cancelar",
      "proj.search": "Buscar por nombre o cliente…",
      "proj.open": "Abrir",
      "proj.delete": "Eliminar",
      "proj.status": "Estado",
      "proj.updated": "Actualizado",
      "proj.confirm_delete": "¿Eliminar este proyecto? No se puede deshacer.",
      "proj.wizard": "Modo Guiado",
      "pd.upload": "Subir plano",
      "pd.upload_hint": "Arrastra un PDF, PNG o JPG aquí, o haz clic para seleccionar.",
      "pd.processing": "Procesando plano…",
      "pd.demo_mode": "MODO DEMO",
      "pd.demo_note": "Las detecciones son simuladas hasta que se despliegue el modelo YOLO.",
      "pd.tab_viewer": "Plano",
      "pd.tab_bom": "BOM",
      "pd.tab_schedule": "Cronograma",
      "pd.tab_compliance": "Normativa",
      "pd.tab_ai": "Análisis IA",
      "pd.tab_legend": "Leyenda",
      "pd.tab_kmz": "Mapa KMZ",
      "pd.tab_plankmz": "Plano + KMZ",
      "pd.tab_survey": "Levantamiento de sitio",
      "pd.tab_route": "Ruta IA",
      // Legend panel
      "leg.title": "Leyenda del plano · auto-extraída",
      "leg.ready": "Listo",
      "leg.running": "Extrayendo… (60-90s)",
      "leg.error_prefix": "Error: ",
      "leg.extract": "Extraer leyenda",
      "leg.re_extract": "Re-extraer",
      "leg.auto_count": "Auto-contar 100%",
      "leg.final_report": "Generar reporte final",
      "leg.aps_bom": "BOM nativo (APS)",
      "leg.filter_all": "Todos",
      "leg.filter_electrical": "Eléctrico",
      "leg.filter_lv": "Low Voltage",
      "leg.add": "Añadir",
      "leg.save_changes": "Guardar cambios",
      "leg.col_symbol": "Símbolo",
      "leg.col_desc": "Descripción",
      "leg.col_category": "Categoría",
      "leg.col_subsystem": "Subsistema",
      "leg.col_code": "Código",
      "leg.empty_none": 'Aún no se ha extraído la leyenda. Click en "Extraer leyenda" arriba.',
      "leg.empty_filter": "No hay símbolos en este filtro.",
      "leg.notes_title": "Notas técnicas extraídas",
      "leg.notes_impact": "afectan BOM",
      "leg.apply_rules": "Aplicar reglas al BOM",
      "leg.counting": "Contando símbolos automáticamente… (60-120s)",
      "leg.counted_ok": "símbolos contados",
      "leg.bom_generated": "líneas de BOM generadas",
      "leg.of_raw": "(de {{raw}} brutos)",
      "leg.pipeline_stage": "Pipeline completo · etapa:",
      "leg.pipeline_ready": "Reporte final generado",
      "leg.pipeline_symbols": "símbolos",
      "leg.pipeline_rules": "reglas aplicadas",
      "leg.mat_total": "Total materiales:",
      "leg.aps_mapping": "Mapeando bloques nativos a la leyenda…",
      "leg.aps_mapped": "bloques mapeados",
      "leg.aps_counted": "objetos contados nativamente",
      "leg.aps_first": "Sube un DWG/RVT primero y ejecuta APS Phase 2 (extract-blocks).",
      "leg.remove_confirm": "Eliminar este símbolo de la leyenda?",
      "leg.vendor_none": "—",
      "leg.symbols_count": "símbolos",
      "leg.notes_count": "notas",
      "leg.toggle_overlay": "Ver hits en el plano",
      "leg.hide_overlay": "Ocultar hits",
      // KMZ panel
      "kmz.title": "KMZ · Google Earth (red de fibra/datos)",
      "kmz.layers_loaded": "capa(s) cargada(s)",
      "kmz.upload": "Subir KMZ",
      "kmz.placemarks": "Placemarks",
      "kmz.paths": "Trayectos",
      "kmz.polygons": "Polígonos",
      "kmz.total_ft": "Total ft",
      "kmz.empty": "Sube un archivo .kmz o .kml de Google Earth para ver la red de fibra/datos sobre el mapa.",
      "kmz.points": "puntos",
      "kmz.node": "Nodo",
      "kmz.path": "Trayecto",
      "pd.detections": "Detecciones",
      "pd.routing": "Rutas",
      "pd.linear_ft": "pies lineales",
      "pd.materials": "Materiales",
      "pd.labor": "Mano de obra",
      "pd.grand_total": "Total general",
      "pd.run_ai": "Ejecutar análisis IA",
      "pd.ai_running": "Analizando con Claude Sonnet 4.5…",
      "pd.export_pdf": "Exportar PDF",
      "pd.export_csv": "Exportar CSV",
      "pd.export_json": "Exportar JSON",
      "pd.push_crm": "Enviar al CRM",
      "pd.new_estimate": "Nueva estimación",
      "pd.annotate": "Anotador",
      "pd.compare": "Comparar",
      "common.compare": "Comparar",
      "bom.category": "Categoría",
      "bom.item": "Ítem",
      "bom.qty": "Cant.",
      "bom.unit": "Unidad",
      "bom.unit_cost": "Costo unit.",
      "bom.total": "Total",
      "bom.save": "Guardar cambios",
      "bom.saved": "BOM guardado",
      "sched.phase": "Fase",
      "sched.duration": "Duración",
      "sched.notes": "Notas",
      "ai.summary": "Resumen ejecutivo",
      "ai.compliance": "Notas de cumplimiento",
      "ai.opt": "Sugerencias de optimización",
      "ai.risks": "Riesgos",
      "ai.empty": "Aún sin análisis IA. Ejecútalo para obtener cumplimiento + optimización.",
      "prices.title": "Biblioteca de precios",
      "prices.subtitle": "Edita costos de material y mano de obra por componente.",
      "prices.save": "Guardar",
      "prices.saved": "Precio actualizado",
      "set.title": "Ajustes",
      "set.crm": "Integración CRM",
      "set.crm_url": "URL del CRM",
      "set.crm_status": "Estado del enlace",
      "set.language": "Idioma",
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["en", "es"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "iqes_lang",
      caches: ["localStorage"],
    },
  });

export default i18n;
